import dayjs from "dayjs";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const CUSTOM_DATE_RANGE = "Custom date range";
export const DEFAULT_DATE_RANGE = "Last week";
export const MAX_CUSTOM_RANGE_YEARS = 1;

export const PRESET_DATE_RANGES = [
  { value: DEFAULT_DATE_RANGE, days: 7, i18nKey: "common.lastWeek" },
  { value: "Last 2 weeks", days: 14, i18nKey: "common.last2Weeks" },
  { value: "Last month", days: 30, i18nKey: "common.lastMonth" },
  { value: "Last quarter", days: 120, i18nKey: "common.lastQuarter" },
  { value: "Last 6 months", days: 182, i18nKey: "common.last6Months" },
  { value: "Last year", days: 365, i18nKey: "common.lastYear" },
];

export const getPresetDays = (value) =>
  PRESET_DATE_RANGES.find((r) => r.value === value)?.days ?? 7;

export const getPresetValue = (days) =>
  PRESET_DATE_RANGES.find((r) => r.days === days)?.value ?? DEFAULT_DATE_RANGE;

// A committed custom range (YYYY-MM-DD strings) covers whole local calendar
// days: from the start of the start date up to the end of the end date, capped
// at "now" so a range ending today never points into the future. Otherwise the
// window is the last `checkPoint` days.
export const getQueryTimestamps = ({
  checkPoint,
  customStart,
  customEnd,
  now = new Date(),
}) => {
  if (customStart && customEnd) {
    const start = dayjs(customStart).startOf("day").toDate();
    const endOfEndDay = dayjs(customEnd).endOf("day").toDate();
    const end = endOfEndDay > now ? now : endOfEndDay;
    return {
      startTimestamp: start.toISOString(),
      endTimestamp: end.toISOString(),
    };
  }
  const start = new Date(now.getTime() - checkPoint * MS_PER_DAY);
  return {
    startTimestamp: start.toISOString(),
    endTimestamp: now.toISOString(),
  };
};

// Product rule: a custom range may span at most MAX_CUSTOM_RANGE_YEARS
// (1 year, matching the longest "Last year" preset), and never extends into
// the future. Any start date up to today can be picked first; once it is
// chosen (`from`), only end dates on/after it and within one year stay
// selectable. When re-editing one side of an existing range, the other side is
// the anchor: the start must be <= end and no more than one year before it, and
// the end must be >= start and no more than one year after it.
export const isCustomRangeDateDisabled = (
  current,
  { from, activeInput, customDateRange, today = dayjs() } = {},
) => {
  if (!current) return false;
  if (current.isAfter(today, "day")) return true;

  if (from) {
    if (current.isBefore(from, "day")) return true;
    return current.isAfter(from.add(MAX_CUSTOM_RANGE_YEARS, "year"), "day");
  }

  if (activeInput === "start" && customDateRange?.[1]) {
    const end = customDateRange[1];
    return (
      current.isAfter(end, "day") ||
      current.isBefore(end.subtract(MAX_CUSTOM_RANGE_YEARS, "year"), "day")
    );
  }
  if (activeInput === "end" && customDateRange?.[0]) {
    const start = customDateRange[0];
    return (
      current.isBefore(start, "day") ||
      current.isAfter(start.add(MAX_CUSTOM_RANGE_YEARS, "year"), "day")
    );
  }
  return false;
};
